/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  JSXElementConstructor,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsAlt, dlsColor} from '../../../theme/theme';
import useThemeContext, {isLegacyTheme} from '../../../theme/useThemeContext';
import {SavedMapViewType} from '../../../types/types';
import {MAP_WIDGET_RIGHT} from '../../constants';
import SavedMapViewsChooserHeader from './SavedMapViewsChooserHeader';
import SavedMapViewsChooserInput from './SavedMapViewsChooserInput';
import SavedMapViewsChooserList from './SavedMapViewsChooserList';
import SavedMapViewsCurrentCard from './SavedMapViewsCurrentCard';

type SavedMapViewsChooserProps = {
  view: __esri.MapView;
  setSavedMapView: Function;
  deleteSavedMapView: Function;
  editSavedMapView: Function;
  setDefaultMapView?: Function;
  onSaveMapView: Function;
  savedMapViews: SavedMapViewType[];
  isTracking?: boolean;
  isFMTracking?: boolean;
  isVisible?: boolean;
  showHeaderAlways?: boolean;
  isOpeningFromBottomSheet?: boolean;
  overideStyle?: GenericObject;
  toggleSavedMapViewTool?: Function;
  closeMapTools?: Function;
  MapControlsBreadCrumbs?: ReactElement<any, string | JSXElementConstructor<any>>;
  rightOffset?: number;
  isTrackingCard?: boolean;
  panelTop?: number;
};

const toggleStyle = (prop: string, defaultvalue: string | number) =>
  (overideStyle) => overideStyle?.[prop] ?? defaultvalue
;

type StyleProps = {
  overideStyle?: any;
  isFMTracking?: boolean;
  rightOffset?: number;
};

const useStyles = makeStyles(theme => ({
  mapView : {
    backgroundColor : dlsColor('white'),
    borderRadius    : '4px 4px 0px 0px',
    boxShadow       : dlsAlt('components', 'boxShadow', 'shadow4'),
    position        : 'absolute',
    top             : (props) => props?.top ?? (isLegacyTheme() ? -8 : 109),
    width           : 300,
    maxHeight       : '100%',
    right           : ({isFMTracking, rightOffset}: StyleProps) =>
      rightOffset || (!isFMTracking ? 55 : MAP_WIDGET_RIGHT)
  },
  mapViewMobile : {
    padding  : '0px 8px 0px 8px',
    top      : toggleStyle('top', 0),
    position : toggleStyle('position', 'inherit')
  }
}));

const nameLimit = 25;

export const shortenName = (name: string) => {
  return `${name?.substring(0, nameLimit)}${name?.length > nameLimit ? '...' : ''}`;
};

/**
 * Reusable component for displaying Saved Map Views
 *
 * ```SavedMapViewsChooser
 *
 *   <SavedMapViewsChooser view={view}
 *     setSavedMapView={setSavedMapView}
 *     deleteSavedMapView={deleteSavedMapView}
 *     onSaveMapView={onSaveMapView}
 *     ....
 *   />
 *
 * ```
 */
const SavedMapViewsChooser = ({
  view,
  rightOffset,
  panelTop,
  isTrackingCard = false,
  isTracking,
  isFMTracking,
  isVisible,
  onSaveMapView,
  toggleSavedMapViewTool,
  isOpeningFromBottomSheet,
  closeMapTools,
  setSavedMapView,
  deleteSavedMapView,
  editSavedMapView,
  setDefaultMapView,
  MapControlsBreadCrumbs,
  overideStyle = {},
  savedMapViews = [],
  showHeaderAlways = false
}: SavedMapViewsChooserProps) => {
  const classes                           = useStyles({overideStyle, rightOffset, isFMTracking});
  const {isCurrent}                       = useThemeContext();
  const [isSaveMapForm, setIsSaveMapForm] = useState(false);
  const [mapViewToEdit, setMapViewToEdit] = useState<SavedMapViewType>(null);

  const viewClass = useMemo(() => {
    return isOpeningFromBottomSheet ? classes.mapViewMobile : classes.mapView;
  }, [isOpeningFromBottomSheet]);

  const resetToolState = useCallback(() => {
    setIsSaveMapForm(false);
    setMapViewToEdit(null);
  }, []);

  useEffect(() => {
    if (!savedMapViews?.length) {
      setIsSaveMapForm(true);
    }
  }, [savedMapViews]);

  const onSave = useCallback(({inputValue}) => {
    if (!mapViewToEdit) {
      const {extent, map: {basemap}} = view;
      onSaveMapView({
        id      : inputValue,
        basemap : basemap?.id,
        extent
      });
    } else {
      editSavedMapView(inputValue, mapViewToEdit);
    }
    resetToolState();
  }, [
    view,
    onSaveMapView,
    mapViewToEdit,
    savedMapViews,
    resetToolState
  ]);

  const onCancel = useCallback(() => {
    resetToolState();
    if (!savedMapViews?.length) {
      toggleSavedMapViewTool?.();
    }
  }, [
    resetToolState,
    toggleSavedMapViewTool,
    savedMapViews
  ]);

  const onDelete = useCallback((mapView, mapViews) => {
    deleteSavedMapView(mapView, mapViews);
  }, [deleteSavedMapView]);

  const onEdit = useCallback((mapView) => {
    setIsSaveMapForm(true);
    setMapViewToEdit(mapView);
  }, []);

  const onSetMapView = useCallback((mapView) => {
    setSavedMapView(mapView);
    resetToolState();
  }, [setSavedMapView]);

  const openSaveMapForm = useCallback(() => {
    setIsSaveMapForm(true);
  }, []);

  const onSetDefaultMapView = useCallback((value: boolean, mapView: SavedMapViewType) => {
    setDefaultMapView?.(value, mapView);
  }, []);

  // the current theme's Tracking map views card, except inside the mobile
  // bottom sheet, which keeps its own breadcrumb layout - every other map
  // keeps the existing panel ...
  const isCurrentCard = isCurrent && isTrackingCard && !isOpeningFromBottomSheet;
  const isFormShown   = isSaveMapForm || !savedMapViews?.length;

  const onCloseCard = function() {
    resetToolState();
    toggleSavedMapViewTool?.();
  };

  return isCurrentCard ? (
    <SavedMapViewsCurrentCard isVisible={isVisible}
      isFormShown={isFormShown}
      isTracking={isTracking}
      panelTop={panelTop}
      rightOffset={rightOffset}
      savedMapViews={savedMapViews}
      mapViewToEdit={mapViewToEdit}
      onOpenSaveForm={openSaveMapForm}
      onClose={onCloseCard}
      onCancel={onCancel}
      onSave={onSave}
      onSetMapView={onSetMapView}
      onDelete={onDelete}
      onEdit={onEdit}
      onSetDefaultMapView={onSetDefaultMapView} />
  ) : (
    <div style={{display: !isVisible ? 'none' : 'inherit'}}
      className={viewClass}>
      <SavedMapViewsChooserHeader isSaveMapForm={isSaveMapForm}
        resetToolState={resetToolState}
        savedMapViews={savedMapViews}
        openSaveMapForm={openSaveMapForm}
        mapViewToEdit={mapViewToEdit}
        showHeaderAlways={showHeaderAlways}
        MapControlsBreadCrumbs={MapControlsBreadCrumbs}/>
      {isSaveMapForm || !savedMapViews?.length ? (
        <SavedMapViewsChooserInput savedMapViews={savedMapViews}
          mapViewToEdit={mapViewToEdit}
          onCancel={onCancel}
          onSave={onSave}/>
      ) : (
        <SavedMapViewsChooserList savedMapViews={savedMapViews}
          isTracking={isTracking}
          onSetMapView={onSetMapView}
          onDelete={onDelete}
          onEdit={onEdit}
          onSetDefaultMapView={onSetDefaultMapView} />
      )}
    </div>
  );
};

export default SavedMapViewsChooser;
